import { applyActionCode, getAuth } from 'firebase/auth';
import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import { TFunction } from 'i18next';

/**
 * Hook to handle email change verification
 * Uses a manual approach to verify email change rather than multiple useEffects
 */
export const useEmailChange = (
    t: TFunction<"translation", undefined>
) => {
    // Call all hooks first, unconditionally
    const { reloadUser, logout } = useAuth();
    const auth = getAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const { showNotification } = useNotification();
    const hasInitializedRef = useRef(false);

    // Function to manually verify email change
    const verifyEmailChange = useCallback(async () => {
        const searchParams = new URLSearchParams(location.search);
        const oobCode = searchParams.get('changeEmailOobCode');
        const email = searchParams.get('email');
        const newEmail = searchParams.get('newEmail');

        if (!oobCode || !email || !newEmail) {
            return false; // Not an email change verification URL
        }

        try {
            // Apply the action code directly using Firebase client SDK
            await applyActionCode(auth, oobCode);

            // Show success notification
            showNotification(t('auth.emailChange.success'), 'success');
            await logout(); // Log out the user after email change
            return true;
        } catch (error: any) {
            console.error('Error changing email:', error);

            // Handle specific Firebase error codes
            if (error.code === 'auth/invalid-action-code') {
                showNotification(t('auth.emailChange.invalidLink'), 'error');
            } else if (error.code === 'auth/user-disabled') {
                showNotification(t('auth.emailChange.accountDisabled'), 'error');
            } else if (error.code === 'auth/user-not-found') {
                showNotification(t('auth.emailChange.userNotFound'), 'error');
            } else {
                showNotification(t('auth.emailChange.error', { message: error.message || 'Unknown error' }), 'error');
            }
            return false;
        }
    }, [location.search, auth, navigate, reloadUser, showNotification, logout]);

    useEffect(() => {
        // Only run the verification logic if not already initialized
        if (!hasInitializedRef.current) {
            const searchParams = new URLSearchParams(location.search);
            const hasEmailChangeParams = searchParams.has('changeEmailOobCode');

            if (hasEmailChangeParams) {
                hasInitializedRef.current = true;
                verifyEmailChange();
            }
        }
    }, [verifyEmailChange, location.search]);

    return { verifyEmailChange };
};
